import { GET_SESSIONS_LIST } from '@/constants/sessions';
import { useQueryCombinedSessions } from '@/hooks/query/useQueryCombinedSessions';
import { GET_SESSIONS, GET_TEAM_MEMBER_SESSIONS } from '@/hooks/query/useQuerySessions';
import {
  GET_INFINITE_SESSIONS,
  GET_INFINITE_TEAM_MEMBER_SESSIONS,
} from '@/hooks/query/useQuerySessionsV2';
import { GET_INFINITE_SESSIONS_WITH_PAGINATION } from '@/hooks/query/useQuerySessionsWithPagination';
import { useSessionRecordingStore } from '@/lib/audio/useSessionRecordingStore/useSessionRecordingStore';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import {
  selectedDateRangeFilterAtom,
  selectedPatientProfilesFilterAtom,
  selectedStatusesFilterAtom,
  useQuerySessionById,
} from '@heidi/session/data';
import { atom, useSetAtom } from 'jotai';
import { useCallback } from 'react';

export const isRefreshingSessionAtom = atom<boolean>(false);

export const clearSessionQueries = () => {
  return Promise.allSettled([
    reactQueryClient.resetQueries({ queryKey: [GET_INFINITE_SESSIONS] }),
    reactQueryClient.resetQueries({ queryKey: [GET_SESSIONS] }),
    reactQueryClient.resetQueries({ queryKey: [GET_INFINITE_TEAM_MEMBER_SESSIONS] }),
    reactQueryClient.resetQueries({ queryKey: [GET_TEAM_MEMBER_SESSIONS] }),
    reactQueryClient.resetQueries({ queryKey: [GET_INFINITE_SESSIONS_WITH_PAGINATION] }),
    reactQueryClient.resetQueries({ queryKey: [GET_SESSIONS_LIST] }),
  ]);
};

export const useRefresh = () => {
  const sessionId = useCurrentSessionId();
  const { refetch: refetchSession, isFetching: isFetchingSession } = useQuerySessionById({
    sessionId,
  });

  const recordingStatus = useSessionRecordingStore((s) => s.status);

  const { isFetching } = useQueryCombinedSessions();

  const isSpinning = isFetching || isFetchingSession;

  const setSelectedStatusFilter = useSetAtom(selectedStatusesFilterAtom);
  const setSelectedPatientProfilesFilter = useSetAtom(selectedPatientProfilesFilterAtom);
  const setSelectedDateRangeFilter = useSetAtom(selectedDateRangeFilterAtom);

  const setIsRefreshingSession = useSetAtom(isRefreshingSessionAtom);

  const handleRefresh = useCallback(async () => {
    setSelectedStatusFilter([]);
    setSelectedPatientProfilesFilter([]);
    setSelectedDateRangeFilter({ from: undefined, to: undefined });
    void clearSessionQueries();

    if (recordingStatus !== 'RECORDING' && sessionId) {
      setIsRefreshingSession(true);
      await refetchSession();
      setIsRefreshingSession(false);
    }
  }, [
    recordingStatus,
    refetchSession,
    sessionId,
    setIsRefreshingSession,
    setSelectedDateRangeFilter,
    setSelectedPatientProfilesFilter,
    setSelectedStatusFilter,
  ]);

  return {
    handleRefresh,
    isSpinning,
  };
};
